import { useCallback, useSyncExternalStore } from "react";
import { getModel, saveModelConfig } from "../../../features/vault/vaultApi";
import { correctConfiguratorKinds, hasChoices, type ConfiguratorDef } from "../../../features/vault/configurator";
import type { EditorStore } from "../../../state/editor/EditorStore";
import { useEditorStore } from "../../../state/editor/useEditor";

/**
 * The configurator being edited in the studio — one per editor, shared by the
 * canvas bar, Basic › Share's switch and the "Customer configurator…" drawer,
 * so the three can never disagree about what a shopper will see.
 *
 * It holds the WHOLE def (parts, prices, model variants) as loaded from the
 * Vault; nothing reaches the Vault until Save. Save re-reads what is stored
 * and writes back only the halves this session changed, so a price set in
 * another tab — or a draft that never loaded — cannot wipe what is saved.
 */

/** Which halves of the def this session has edited. */
type Touched = "groups" | "pricing" | "variants";

export interface DraftState {
  /** The Vault model the draft belongs to; undefined on /dev/studio. */
  modelId: string | undefined;
  load: "idle" | "loading" | "ready" | "error";
  def: ConfiguratorDef;
  /** JSON of the def as stored, for "has anything changed?". */
  savedJson: string;
  /** The canvas bar is showing. */
  on: boolean;
  /** The slot (viewer group id) whose choices the bar shows. */
  activeSlot: string | null;
  /** The "+" picker is open on the active slot. */
  pickerOpen: boolean;
  saving: boolean;
}

const EMPTY: ConfiguratorDef = { groups: [] };

export type SaveResult = "saved" | "no-model";

export class ConfiguratorDraft {
  private state: DraftState = {
    modelId: undefined,
    load: "idle",
    def: EMPTY,
    savedJson: JSON.stringify(EMPTY),
    on: false,
    activeSlot: null,
    pickerOpen: false,
    saving: false,
  };
  private touched = new Set<Touched>();
  private listeners = new Set<() => void>();
  /** Bumped per bind, so a slow load for a model we left cannot land. */
  private generation = 0;

  getState = (): DraftState => this.state;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  private set(patch: Partial<DraftState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  get dirty(): boolean {
    return JSON.stringify(this.state.def) !== this.state.savedJson;
  }

  /**
   * Point the draft at the studio's Vault model (or none) and load what it has
   * saved. A model that already has customer choices turns the bar on.
   */
  bindModel(modelId: string | undefined) {
    if (modelId === this.state.modelId && this.state.load !== "idle") return;
    const gen = ++this.generation;
    this.touched.clear();
    this.set({ modelId, def: EMPTY, savedJson: JSON.stringify(EMPTY), activeSlot: null, pickerOpen: false, load: modelId ? "loading" : "ready" });
    if (!modelId) return;
    void this.fetchSaved(modelId).then(
      (saved) => {
        if (gen !== this.generation) return;
        const def = saved ?? EMPTY;
        this.set({ def, savedJson: JSON.stringify(def), load: "ready", on: this.state.on || hasChoices(def) });
      },
      () => {
        if (gen === this.generation) this.set({ load: "error" });
      },
    );
  }

  /** Try the load again after an error. */
  reload() {
    const id = this.state.modelId;
    this.state = { ...this.state, load: "idle" };
    this.bindModel(id);
  }

  /** Forget local edits — a different piece was loaded into /dev/studio. */
  reset() {
    this.touched.clear();
    this.set({ def: EMPTY, savedJson: JSON.stringify(EMPTY), activeSlot: null, pickerOpen: false });
  }

  setOn(on: boolean) {
    this.set({ on, pickerOpen: on ? this.state.pickerOpen : false });
  }

  setActiveSlot(activeSlot: string | null) {
    this.set({ activeSlot });
  }

  setPickerOpen(pickerOpen: boolean) {
    this.set({ pickerOpen });
  }

  /** Change the def. `what` records which half changed, for Save's merge. */
  edit(what: Touched, fn: (def: ConfiguratorDef) => ConfiguratorDef) {
    const def = fn(this.state.def);
    if (def === this.state.def) return;
    this.touched.add(what);
    this.set({ def });
  }

  /**
   * Write the draft to the Vault. Returns "no-model" when the studio is not
   * open on a Vault model (/dev/studio): there is nowhere to save it.
   */
  async save(): Promise<SaveResult> {
    const { modelId } = this.state;
    if (!modelId) return "no-model";
    const gen = this.generation;
    this.set({ saving: true });
    try {
      const stored = (await this.fetchSaved(modelId)) ?? EMPTY;
      const { def } = this.state;
      const merged: ConfiguratorDef = {
        ...stored,
        groups: this.touched.has("groups") ? def.groups : stored.groups ?? [],
        ...(this.touched.has("pricing") ? { basePrice: def.basePrice, currency: def.currency } : {}),
        ...(this.touched.has("variants") ? { variants: def.variants } : {}),
      };
      await saveModelConfig(modelId, { configurator: merged });
      if (gen === this.generation) {
        this.touched.clear();
        this.set({ def: merged, savedJson: JSON.stringify(merged) });
      }
      return "saved";
    } finally {
      this.set({ saving: false });
    }
  }

  /**
   * The def as stored, read back with each part's kind as its NAME says
   * (`correctConfiguratorKinds`): a def saved while "Gem 01" was showing as
   * metal opens as a stone slot again, and is saved that way on the next Save.
   */
  private async fetchSaved(modelId: string): Promise<ConfiguratorDef | undefined> {
    const model = await getModel(modelId);
    const def = (model.config as { configurator?: ConfiguratorDef } | undefined)?.configurator;
    return def && Array.isArray(def.groups) ? correctConfiguratorKinds(def) : undefined;
  }
}

const drafts = new WeakMap<EditorStore, ConfiguratorDraft>();

/** The one draft per editor store. */
export function configuratorDraftFor(store: EditorStore): ConfiguratorDraft {
  let d = drafts.get(store);
  if (!d) {
    d = new ConfiguratorDraft();
    drafts.set(store, d);
  }
  return d;
}

/** The editor's draft and its current state, re-rendering on every change. */
export function useConfiguratorDraft(): [DraftState, ConfiguratorDraft] {
  const draft = configuratorDraftFor(useEditorStore());
  const state = useSyncExternalStore(
    useCallback((cb: () => void) => draft.subscribe(cb), [draft]),
    draft.getState,
  );
  return [state, draft];
}

/**
 * The bar asks its HOST to open the configurator drawer (prices, names, model
 * variants) by name — the drawer is Vault host code (`app/pages`), the same
 * way TheGoldsmith's dialog is asked for (`basic/events.ts`).
 */
export const OPEN_CONFIGURATOR_DETAILS_EVENT = "studio:open-configurator";

/**
 * The Vault's top-bar menu asks for the customer link by name too: the draft
 * (which must be saved first) lives inside the editor, the menu outside it.
 * Dispatched synchronously from the click, so the window it opens is still
 * the user's own gesture and no popup blocker stops it.
 */
export const OPEN_CUSTOMER_LINK_EVENT = "studio:open-customer-link";
